/**
 * BookingFormShell — V2 engine. Renders the dialog body, tabs strip,
 * and stitches together registry-driven fields + slot-rendered blocks
 * + always-visible footer.
 *
 * Lives under `?bf=v2`. Pure presentation — all state in useBookingForm.
 */
import { useMemo, useState, useEffect } from "react";
import React from "react";
import { DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import BookingStatusActions from "@/components/BookingStatusActions";
import { AlertCircle, ChevronDown, Copy, FileText, Activity as ActivityIcon, Printer } from "lucide-react";
import BookingActivityFeed from "@/components/BookingActivityFeed";
import DriverSiteInfoLink from "@/components/DriverSiteInfoLink";
import { applyTabOverrides, applyFieldOverrides, applySlotOverrides } from "@/lib/api";
import { supabase } from "@/lib/supabaseClient";
import { Field, Dot } from "./_shared";
import { useBookingForm } from "./hooks/useBookingForm";
import { SlotLabelProvider } from "./hooks/useSlotLabels";
import { TABS } from "./registry/tabs";
import { FIELDS } from "./registry/fields";
import { buildFormVisibility } from "@/lib/formVisibility";
import { slotsForTab } from "./registry/slots";
import { COMPONENTS } from "./components";
import StatusTab from "./StatusTab";
import { composeInSiteTz } from "@/lib/datetime";

export default function BookingFormShell({
  booking, defaultSiteId, defaultBayId, prefill, onOpenChange, onSaved,
}) {
  const ef = useBookingForm({ booking, defaultSiteId, defaultBayId, prefill, onSaved, onOpenChange });
  const {
    values, set, setValues, tab, setTab, error, saving, isEdit, user,
    sites, bays, picks, flds, reservationTypes, fieldOverrides, fieldGroups,
    selectedSite, selectedBay, missing, missingByTab, handleSubmit,
    hasRole, assignments,
  } = ef;

  // ── Phase 9.5 — Status tab gate ─────────────────────────────────────
  //   Show when: (a) at least one CC recipient exists for this tenant
  //   (Email Handling has been configured), AND (b) the current user
  //   role is client-facing (booker / client_admin / site_admin).
  const clientIdForGate =
    booking?.client_id
    || (assignments?.[0]?.client_id)
    || null;
  const [hasEmailCC, setHasEmailCC] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!clientIdForGate) { setHasEmailCC(false); return; }
    (async () => {
      const { count } = await supabase
        .from("lbs_email_cc_recipients")
        .select("id", { count: "exact", head: true })
        .eq("client_id", clientIdForGate)
        .eq("is_active", true);
      if (!cancelled) setHasEmailCC((count || 0) > 0);
    })();
    return () => { cancelled = true; };
  }, [clientIdForGate]);
  const isClientUser = hasRole?.("booker")
                    || hasRole?.("client_admin")
                    || hasRole?.("site_admin")
                    || hasRole?.("super_admin");
  // Status tab only makes sense on an existing booking (edit mode) —
  // there's nothing "outstanding" until the booking has been saved.
  const showStatusTab = hasEmailCC && !!isClientUser && isEdit;

  // ── Phase 9.6 — Booker post-approval edit whitelist ────────────────
  // Fetch the tenant's allowed-fields list. When the current user is a
  // booker (only) AND the booking is APPROVED/CONFIRMED, fields not in
  // this list are rendered read-only (pointer-events-none, opacity-60).
  const [editWhitelist, setEditWhitelist] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!clientIdForGate) { setEditWhitelist([]); return; }
    (async () => {
      const { data } = await supabase
        .from("lbs_client_settings")
        .select("booker_editable_fields")
        .eq("client_id", clientIdForGate)
        .maybeSingle();
      if (!cancelled) setEditWhitelist(data?.booker_editable_fields || []);
    })();
    return () => { cancelled = true; };
  }, [clientIdForGate]);

  const LOCKED_STATUSES = new Set(["approved", "confirmed", "checked_in", "completed"]);
  const bookerOnly = hasRole?.("booker")
                     && !hasRole?.("client_admin")
                     && !hasRole?.("site_admin")
                     && !hasRole?.("super_admin");
  const applyEditLockdown = bookerOnly
                         && isEdit
                         && !!booking?.status
                         && LOCKED_STATUSES.has(booking.status);
  const isFieldLocked = (fieldKey) =>
    applyEditLockdown && !editWhitelist.includes(fieldKey);

  // Apply per-tenant overrides on top of the static registry.
  const effectiveFields = applyFieldOverrides(FIELDS, fieldOverrides);
  const contentTabs     = applyTabOverrides(TABS, fieldOverrides)
                          .filter((t) => !t.editOnly)
                          .filter((t) => t.key !== "status" || showStatusTab);
  const tabsToShow      = isEdit
    ? applyTabOverrides(TABS, fieldOverrides)
        .filter((t) => t.key !== "status" || showStatusTab)
    : contentTabs;

  // For one tab, return { ungrouped: [...items], groups: [{label, fields:[]}] }.
  //
  // Phase 7.2 — implicit indent groups:
  //   A field with `group_key` set that doesn't match any row in the
  //   `lbs_booking_field_groups` table is treated as an INDENTED CHILD.
  //   Its head is the most recent root-level field above it (in position
  //   order). We don't materialise the relationship into a synthetic
  //   "group" — instead we annotate the child field entry with
  //   `__indented: true` and `renderItem` adds a left-rule + padding.
  const partitionTab = (tabKey) => {
    const visibleSlots = applySlotOverrides(slotsForTab(tabKey), fieldOverrides);
    const slots = visibleSlots.map((s) => ({ kind: "slot", item: s }));
    const tabFields = effectiveFields.filter((f) => f.tab === tabKey);

    // Backward-compat: named groups loaded from lbs_booking_field_groups.
    const tabGroups = (fieldGroups || [])
      .filter((g) => g.tab_key === tabKey && g.is_visible !== false)
      .slice()
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const namedGroupKeys = new Set(tabGroups.map((g) => g.group_key));

    // Field is "in a named group" only if its group_key exists as a row.
    const inNamedGroup = (f) => f.group_key && namedGroupKeys.has(f.group_key);

    // Everything else (no group_key OR implicit indent group) goes into
    // `ungrouped`. Annotate implicit children with __indented.
    const rest = tabFields.filter((f) => !inNamedGroup(f));
    rest.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const restEntries = rest.map((f) => ({
      kind: "field",
      item: f,
      __indented: !!f.group_key,    // any leftover group_key = implicit child
    }));

    const ungrouped = [...slots, ...restEntries].sort(
      (a, b) => (a.item.position ?? 0) - (b.item.position ?? 0)
    );

    // Phase 8.2: section dividers from override rows.
    const dividerEntries = (fieldOverrides || [])
      .filter((o) => o.scope === "divider"
                  && o.tab_key === tabKey
                  && o.is_visible !== false)
      .map((o) => ({
        kind: "divider",
        item: { key: o.field_key, label: o.label || "", position: o.position ?? 0 },
      }));
    const merged = [...ungrouped, ...dividerEntries].sort(
      (a, b) => (a.item.position ?? 0) - (b.item.position ?? 0)
    );

    const groups = tabGroups.map((g) => ({
      key:   g.group_key,
      label: g.label,
      fields: tabFields
        .filter((f) => f.group_key === g.group_key)
        .slice()
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map((f) => ({ kind: "field", item: f })),
    }));
    return { ungrouped: merged, groups };
  };

  // Cross-tab visibility: slots that summarise other tabs (QA confirm cards,
  // FLD block) must follow the tenant's tab / slot / field switches.
  const visibility = useMemo(() => buildFormVisibility(fieldOverrides), [fieldOverrides]);

  // Build the ctx grab-bag — slots read this for tab-scoped extras.
  const ctx = useMemo(() => ({
    sites, bays, picks, flds, reservationTypes,
    selectedSite, selectedBay,
    booking, prefill, isEdit, user,
    clientId: selectedSite?.client_id || booking?.client_id || null,
    visibility,
  }), [sites, bays, picks, flds, reservationTypes, selectedSite, selectedBay, booking, prefill, isEdit, user, visibility]);

  const renderItem = (entry, currentTab) => {
    if (entry.kind === "divider") {
      // Phase 8.2: section divider — bold uppercase mini-heading, no rule line.
      return (
        <div key={`d:${entry.item.key}`} className="sm:col-span-2 pt-3 pb-1"
             data-testid={`booking-divider-${entry.item.key}`}>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
            {entry.item.label}
          </div>
        </div>
      );
    }
    if (entry.kind === "field") {
      const f = entry.item;
      const Cmp = COMPONENTS[f.component];
      if (!Cmp) return null;
      const indentCls = entry.__indented ? "pl-4 border-l-2 border-slate-200 ml-2" : "";
      // Phase 9.6: read-only wrap when booker + approved + not whitelisted.
      const locked = isFieldLocked(f.key);
      const lockedCls = locked ? "pointer-events-none opacity-60 select-none" : "";
      return (
        <div key={`f:${f.key}`}
             className={`${f.fullWidth ? "sm:col-span-2" : ""} ${indentCls} ${lockedCls}`.trim()}
             {...(locked ? { "data-testid": `locked-field-${f.key}`, title: "Editable only via 'Request update' — see admin" } : {})}>
          <Cmp field={f} value={values[f.key]} set={set} picks={picks} />
          {f.help_text && (
            <p className="text-xs text-slate-500 mt-1" data-testid={`booking-help-${f.key}`}>
              {f.help_text}
            </p>
          )}
        </div>
      );
    }
    const s = entry.item;
    const SlotCmp = s.component;
    const extra = s.componentProps || {};
    const slotCtx = { ...ctx, currentTab };
    return (
      <div key={`s:${s.key}`} className={s.fullWidth ? "sm:col-span-2" : ""}>
        <SlotCmp values={values} set={set} setValues={setValues} picks={picks} ctx={slotCtx} {...extra} />
      </div>
    );
  };

  return (
    <SlotLabelProvider overrides={fieldOverrides}>
      <DialogHeader>
        <DialogTitle>
          {isEdit ? `Edit booking ${booking?.reference_number || ""}` : "New booking"}
        </DialogTitle>
        <DialogDescription>
          Capture vehicle, goods, restraint and compliance details. A reference
          number is generated automatically on save.
        </DialogDescription>
      </DialogHeader>

      {isEdit && booking && (
        <>
          <BookingStatusActions booking={booking} onChanged={(u) => onSaved?.(u)} />
          <DriverSiteInfoLink booking={booking} />
        </>
      )}

      {applyEditLockdown && (
        <Alert className="border-amber-300 bg-amber-50" data-testid="booker-edit-lockdown-banner">
          <AlertCircle className="h-4 w-4 text-amber-700" />
          <AlertDescription className="text-amber-900 text-sm">
            This booking is <strong>{booking?.status}</strong>. You can only edit{" "}
            {editWhitelist.length === 0 ? (
              <><strong>no</strong> fields on this booking. Use <em>Request update</em>{" "}
              to ask an admin to make changes.</>
            ) : (
              <><strong>{editWhitelist.length} whitelisted field
              {editWhitelist.length === 1 ? "" : "s"}</strong> here. For any other
              change, use <em>Request update</em>.</>
            )}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <RuleViolationBanner error={error} values={values} selectedSite={selectedSite} />}

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList
            className="grid w-full h-auto"
            style={{ gridTemplateColumns: `repeat(${tabsToShow.length}, minmax(0, 1fr))` }}
            data-testid="booking-tabs"
          >
            {tabsToShow.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>
                {t.label}
                {missingByTab[t.key] && <Dot testId={`tab-missing-dot-${t.key}`} />}
                {t.key === "status" && values.driver_tba && (
                  <AlertCircle className="h-3 w-3 ml-1 text-amber-600 inline"
                               data-testid="status-tab-outstanding-icon" />
                )}
              </TabsTrigger>
            ))}
          </TabsList>

          {contentTabs.map((t) => {
            // Phase 9.5 — Status is a custom-rendered tab. Skip the
            // normal partition/grid pipeline and render StatusTab.
            if (t.custom && t.key === "status") {
              return (
                <TabsContent key={t.key} value={t.key} className="pt-3"
                             data-testid={`${t.key}-tab-content`}>
                  <StatusTab
                    effectiveFields={effectiveFields}
                    values={values}
                    set={set}
                    onJumpToTab={(k) => setTab(k)}
                  />
                </TabsContent>
              );
            }
            const { ungrouped, groups } = partitionTab(t.key);
            return (
              <TabsContent key={t.key} value={t.key} className="space-y-3 pt-3" data-testid={`${t.key}-tab-content`}>
                {ungrouped.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {ungrouped.map((entry) => renderItem(entry, t.key))}
                  </div>
                )}
                {groups.map((g) => (
                  <fieldset
                    key={`grp:${g.key}`}
                    data-testid={`group-${t.key}-${g.key}`}
                    className="border-t border-slate-200 pt-3 mt-2"
                  >
                    <legend className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2 px-1">
                      {g.label}
                    </legend>
                    {g.fields.length > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {g.fields.map((entry) => renderItem(entry, t.key))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic px-1">No fields in this group yet.</p>
                    )}
                  </fieldset>
                ))}
              </TabsContent>
            );
          })}

          {isEdit && booking && (
            <TabsContent value="activity" className="pt-3" data-testid="activity-tab-content">
              <BookingActivityFeed bookingId={booking.id} />
            </TabsContent>
          )}
        </Tabs>

        <div className="space-y-3 border-t pt-4">
          <Field label="Booking notes (visible to managers)">
            <Textarea rows={2} data-testid="booking-notes-input"
                      value={values.notes}
                      onChange={(e) => set("notes", e.target.value)} />
          </Field>
        </div>

        <DialogFooter className="sm:justify-between gap-2">
          {/* Bottom-left: Print action (edit mode only). Two-item dropdown
              when the booking is checked-in or beyond (so the forklift
              operator can be handed a paper Activity/handoff sheet
              separately from the full booking); single item otherwise. */}
          {isEdit && booking?.id ? (
            <PrintMenu booking={booking} />
          ) : (
            <div /> // spacer so Cancel/Save stay right-aligned
          )}

          <div className="flex items-center gap-2">
            {!isEdit && missing.length > 0 && (
              <div className="text-xs text-rose-600" data-testid="booking-missing">
                Still required: {missing.map((m) => m.label).join(", ")}
              </div>
            )}
            {isEdit && missing.length > 0 && (
              <div className="text-xs text-amber-700" data-testid="booking-missing-edit">
                Still to complete: {missing.map((m) => m.label).join(", ")}
              </div>
            )}
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              type="submit"
              disabled={saving || (!isEdit && missing.length > 0)}
              data-testid="booking-save-btn"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create booking"}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </SlotLabelProvider>
  );
}


/**
 * Renders a dressier error banner. Detects the specific
 * booking-rule-enforcement errors raised by migration 097's
 * lbs_bookings_enforce_site_rules() trigger and offers a one-click
 * "Copy admin request" that puts a pre-composed override request on
 * the clipboard for the booker to paste into email/Slack. Any other
 * error falls back to the plain destructive alert.
 */
function RuleViolationBanner({ error, values, selectedSite }) {
  const msg = String(error || "");
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  // Phrases used by the trigger's RAISE EXCEPTION calls in 097 + 098.
  const rulePatterns = [
    /outside site operating hours/i,
    /outside site overnight operating window/i,
    /outside bay operating hours/i,
    /overlaps a blackout/i,
    /exceeds the bay maximum/i,
    /not a multiple of the/i,
    /must be at least .* minutes long/i,
    /must be after start/i,
  ];
  const isRule = rulePatterns.some((r) => r.test(msg));

  if (!isRule) {
    return (
      <Alert variant="destructive" data-testid="booking-form-error">
        <AlertDescription>{msg}</AlertDescription>
      </Alert>
    );
  }

  const cleanMsg = msg.replace(/^.*?ERROR:\s*/, "");

  const requestText =
    `Hi — I need an admin to place this booking for me because it violates a site rule.\n\n` +
    `Site: ${selectedSite?.name || values.site_id || "—"}\n` +
    `Date: ${values.booking_date}\n` +
    `Time: ${values.start_time} – ${values.end_time}\n` +
    `Reason: ${cleanMsg}\n\n` +
    `Booker: ${values.sender_name || ""} ${values.sender_company ? `(${values.sender_company})` : ""}`.trim();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(requestText);
      const { toast } = await import("sonner");
      toast?.success("Override request copied — paste into your email or chat to a dock manager.");
    } catch {
      /* clipboard may be blocked in preview iframe */
    }
  };

  const sendRequest = async () => {
    if (!selectedSite?.client_id || !selectedSite?.id) {
      const { toast } = await import("sonner");
      toast?.error("Pick a site first.");
      return;
    }
    setSending(true);
    try {
      const { createOverrideRequest } = await import("@/lib/api");
      const tz = selectedSite.timezone || values._booking_tz;
      const [yy, mo, dd] = values.booking_date.split("-").map(Number);
      const at = (t) => {
        const [h, m] = (t || "00:00").split(":").map(Number);
        return tz ? composeInSiteTz({ year: yy, month: mo, day: dd, hour: h, minute: m }, tz)
                  : new Date(`${values.booking_date}T${t}:00`).toISOString();
      };
      const isoStart = at(values.start_time);
      const isoEnd   = at(values.end_time);
      const { error: e } = await createOverrideRequest({
        client_id:       selectedSite.client_id,
        site_id:         selectedSite.id,
        bay_id:          values.bay_id || null,
        scheduled_start: isoStart,
        scheduled_end:   isoEnd,
        rule_error:      cleanMsg,
        booking_payload: values,
      });
      const { toast } = await import("sonner");
      if (e) { toast?.error(e.message); return; }
      setSent(true);
      toast?.success("Override request sent — a dock manager has been notified.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Alert variant="destructive" data-testid="booking-form-rule-error">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription className="space-y-2">
        <div className="font-semibold">Site rule blocks this booking</div>
        <div className="text-sm">{cleanMsg}</div>
        <div className="text-xs opacity-90">
          Admins (client_admin, site_admin, dock_manager) can overrule this rule.
        </div>
        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            onClick={sendRequest}
            disabled={sending || sent}
            className="inline-flex items-center gap-1.5 text-xs font-medium bg-white text-rose-800 px-2.5 py-1 rounded border border-rose-300 hover:bg-rose-50 disabled:opacity-60"
            data-testid="rule-send-override-request"
          >
            {sent ? "Sent ✓" : sending ? "Sending…" : "Send override request"}
          </button>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1.5 text-xs font-medium bg-white/95 text-rose-800 px-2.5 py-1 rounded border border-rose-300 hover:bg-white"
            data-testid="rule-copy-override-request"
          >
            <Copy className="h-3.5 w-3.5" /> Copy for email
          </button>
        </div>
      </AlertDescription>
    </Alert>
  );
}


/**
 * PrintMenu — dropdown at the bottom-left of the booking DialogFooter.
 *
 * Behaviour:
 *   • Always offers "Print full booking" (all sections except Activity).
 *   • Additionally offers "Print activity log" when the booking has
 *     arrived / is in progress / completed — so a site can hand the
 *     forklift operator a paper Activity sheet as the load-acceptance
 *     record when the electronic checklist isn't practical.
 *
 * Uses `/bookings/:id/print` with an optional `?view=activity` query
 * param — the print page reads it and switches templates.
 */
const CHECKED_IN_STATUSES = new Set(["checked_in", "loading", "unloading", "completed"]);

function PrintMenu({ booking }) {
  const isCheckedIn = CHECKED_IN_STATUSES.has(booking?.status);
  const openPrint = (view) => {
    const qs = view ? `?view=${view}` : "";
    window.open(`/bookings/${booking.id}/print${qs}`, "_blank", "noopener");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="booking-print-btn"
          title="Open a printable / PDF-ready view of this booking"
        >
          <Printer className="h-4 w-4 mr-1.5" /> Print
          <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">
          Print
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => openPrint(null)}
          data-testid="print-full-booking"
          className="gap-2"
        >
          <FileText className="h-4 w-4" />
          <div className="flex-1">
            <div>Full booking</div>
            <div className="text-xs text-slate-500">All sections except Activity</div>
          </div>
        </DropdownMenuItem>
        {isCheckedIn && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => openPrint("activity")}
              data-testid="print-activity-log"
              className="gap-2"
            >
              <ActivityIcon className="h-4 w-4" />
              <div className="flex-1">
                <div>Activity log only</div>
                <div className="text-xs text-slate-500">Handoff sheet for forklift operator</div>
              </div>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
